import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Timeframe config ─────────────────────────────────────────────────────────
const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1D'];

// Poll interval (ms) per timeframe — roughly 1/4 of the bar period so the
// current bar feels live without hammering the server.
const POLL_MS = {
  '1m':  30000,
  '5m':  60000,
  '15m': 120000,
  '30m': 180000,
  '1h':  300000,
  '4h':  600000,
  '1D':  900000,
};

// Session coloring only makes sense on intraday timeframes.
const SESSION_TF = new Set(['1m', '5m', '15m', '30m', '1h']);

// ── Session colors (solid hex per user spec) ─────────────────────────────────
const BG_MAIN      = '#0F0F0F';  // main market — also the chart background
const BG_PREMARKET = '#523A15';
const BG_POSTMKT   = '#131C33';

// ── Session classification (ET) ──────────────────────────────────────────────
function getSessionType(unixTs) {
  const d  = new Date(unixTs * 1000);
  const et = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const m  = et.getHours() * 60 + et.getMinutes();
  if (m >= 570 && m < 960)  return 'regular';
  if (m >= 240 && m < 570)  return 'premarket';
  if (m >= 960 && m < 1200) return 'postmarket';
  return null;
}

// ── Build session bands from bar array ───────────────────────────────────────
function buildSessionBands(bars) {
  const bands = [];
  if (!bars || !bars.length) return bands;

  let curType = null, bandStart = null, bandEnd = null;

  const flush = () => {
    if (curType !== null) {
      bands.push({ type: curType, start: bandStart, end: bandEnd + 60 });
    }
  };

  for (const bar of bars) {
    const t = getSessionType(bar.time);
    if (t !== curType) { flush(); curType = t; bandStart = bar.time; }
    bandEnd = bar.time;
  }
  flush();
  return bands;
}

// ── Lightweight Charts v5 ISeriesPrimitive for session backgrounds ───────────
//
//  IPrimitivePaneView.renderer() returns IPrimitivePaneRenderer which has:
//    draw(target)            — main z-ordered layer
//    drawBackground(target)  — always behind series content (ideal for shading)
//
//  target.useMediaCoordinateSpace() gives a CSS-pixel context; timeToCoordinate()
//  already returns CSS-pixel values so no pixel-ratio scaling needed.
//
class SessionBandPrimitive {
  constructor() {
    this._bands = [];
    this._chart = null;
    this._requestUpdate = null;
  }

  setBands(bands) {
    this._bands = bands;
    if (this._requestUpdate) this._requestUpdate();
  }

  clearBands() {
    this._bands = [];
    if (this._requestUpdate) this._requestUpdate();
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
  }

  updateAllViews() {}

  paneViews() {
    return [this._view()];
  }

  _view() {
    const self = this;
    return {
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},

        drawBackground: (target) => {
          if (!self._chart || !self._bands.length) return;

          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const ts  = self._chart.timeScale();
            const W   = scope.mediaSize.width;
            const H   = scope.mediaSize.height;

            for (const band of self._bands) {
              const color =
                band.type === 'premarket'  ? BG_PREMARKET :
                band.type === 'postmarket' ? BG_POSTMKT   : BG_MAIN;

              const x1 = ts.timeToCoordinate(band.start);
              const x2 = ts.timeToCoordinate(band.end);

              if (x1 === null && x2 === null) continue;

              const left  = x1 !== null ? Math.max(0, x1) : 0;
              const right = x2 !== null ? Math.min(W, x2) : W;
              if (right <= left) continue;

              ctx.fillStyle = color;
              ctx.fillRect(left, 0, right - left, H);
            }
          });
        },
      }),
    };
  }
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ChartWidget() {
  const [tf, setTf] = useState('1m');

  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const seriesRef     = useRef(null);
  const primitiveRef  = useRef(null);
  const pollRef       = useRef(null);
  const mountedRef    = useRef(true);
  const tfRef         = useRef(tf);         // stable ref for async callbacks
  const initialDone   = useRef(false);

  // Keep tfRef in sync with state
  useEffect(() => { tfRef.current = tf; }, [tf]);

  // ── Create chart once on mount ─────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: 'solid', color: BG_MAIN },
        textColor:  '#9ca3af',
        fontSize:   11,
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      rightPriceScale: { borderColor: '#2d2d2d' },
      timeScale: {
        borderColor:    '#2d2d2d',
        timeVisible:    true,
        secondsVisible: false,
        rightOffset:    8,
      },
      crosshair: {
        vertLine: { color: '#555', width: 1, style: 0 },
        horzLine: { color: '#555', width: 1, style: 0 },
      },
      width:  containerRef.current.clientWidth,
      height: 400,
    });
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor:         '#26a69a',
      downColor:       '#ef5350',
      borderUpColor:   '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor:     '#26a69a',
      wickDownColor:   '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    const handleResize = () => {
      if (containerRef.current && mountedRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('resize', handleResize);
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = null;
    };
  }, []); // chart created once

  // ── Refetch whenever timeframe changes ────────────────────────────────────
  useEffect(() => {
    initialDone.current = false;
    clearInterval(pollRef.current);

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/bars?symbol=SPY&tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!bars || !bars.length || !mountedRef.current) return;

        if (isInitial || !initialDone.current) {
          seriesRef.current.setData(bars);
          chartRef.current.timeScale().scrollToRealTime();
          initialDone.current = true;
        } else {
          seriesRef.current.update(bars[bars.length - 1]);
        }

        // Session bands only for intraday timeframes
        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(bars));
        } else {
          primitiveRef.current?.clearBands();
        }
      } catch {
        // ignore — stale data shown until next poll
      }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 30000);

    return () => clearInterval(pollRef.current);
  }, [tf]);

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      {/* Header ── ticker + timeframe buttons + legend */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">

        {/* Ticker label */}
        <span
          className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
        >
          SPY
        </span>

        {/* Timeframe buttons */}
        <div className="flex items-center gap-1">
          {TIMEFRAMES.map(t => (
            <button
              key={t}
              onClick={() => setTf(t)}
              className="text-xs font-medium px-2 py-1 rounded transition-colors"
              style={
                t === tf
                  ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
                  : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
              }
            >
              {t}
            </button>
          ))}
        </div>

        {/* Legend (only shown for intraday timeframes) */}
        {SESSION_TF.has(tf) && (
          <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: BG_PREMARKET, border: '1px solid #7a5520' }} />
              Pre-market
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: BG_POSTMKT, border: '1px solid #1e2d50' }} />
              Post-market
            </span>
          </div>
        )}
      </div>

      {/* Chart canvas */}
      <div ref={containerRef} />
    </div>
  );
}
